import React, { useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { NgwAvatar } from "../../components/common";
import { Bell } from "lucide-react";
import { DateTimeUtil, getNotificationStatusLabel } from "../../utils";

const StandardNotificationDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const notification = useSelector((state: RootState) => state.mobileNotifications.selectedNotification);

    const mi = notification?.message_info as any;
    const title = mi?.title || mi?.TITLE || "";
    const body = mi?.body || mi?.MESSAGE || "";

    const requestor = notification?.requestor;
    const requestorName = requestor?.emp_name;
    const requestorImage = requestor?.image_url;

    const currentStatus = notification?.workflow_current_step_details?.current_status;
    const statusText = notification?.workflow_current_step_details?.status_text;
    const statusInfo = getNotificationStatusLabel(currentStatus);

    const notificationDateTime = notification?.notification_date_time
        ? DateTimeUtil.FormatWithDayjsUtc(
              DateTimeUtil.convertUtcToLocalTime(notification.notification_date_time),
              DateTimeUtil.DATETIME_FANCY_2
          )
        : null;

    useLayoutEffect(() => {
        if (title) dispatch(setTopNavNameTitle(title));
    }, [dispatch, title]);

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">

                {/* Header card: icon + title + body + metadata */}
                <div className="bg-white mx-4 mt-4 rounded-lg shadow-sm p-4 mb-3 border-l-4 border-l-blue-500">
                    <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Bell className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            {title && <h1 className="text-lg font-bold text-gray-900 mb-1 leading-snug">{title}</h1>}
                            {body && <p className="text-gray-600 text-sm leading-relaxed">{body}</p>}
                            {(notificationDateTime || statusText) && (
                                <div className="flex items-center flex-wrap gap-1.5 mt-3">
                                    {notificationDateTime && (
                                        <span className="text-xs text-gray-400">{notificationDateTime}</span>
                                    )}
                                    {notificationDateTime && statusText && (
                                        <span className="text-gray-300 text-xs">·</span>
                                    )}
                                    {statusText && (
                                        <span className={`text-xs font-medium ${statusInfo.color}`}>{statusText}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Requestor card */}
                {requestorName && (
                    <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3 border-l-4 border-l-purple-400">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Employee</p>
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12">
                                <NgwAvatar src={requestorImage} name={requestorName} />
                            </div>
                            <span className="text-base font-semibold text-gray-900">{requestorName}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StandardNotificationDetail;
